// Resolve a composition `{kind:'attachment'}` image src to a DISPLAY url for the
// client canvas. The composition stores the stable attachmentId (the server re-signs
// it to a fresh url before any worker render); the client just needs something to put
// in an <img src> right now. Resolution order:
//   1. the brand-kit store (brand photos carry a fresh publicUrl, re-fetched per
//      business load) — covers "pick from brand library", including after a reload;
//   2. a module-level cache seeded on pick/upload — covers a just-chosen photo
//      instantly, no round-trip;
//   3. the owner-scoped download-url endpoint — covers an uploaded photo that is not
//      in the brand kit after a reload.

import { OpenAPI } from 'openapi'
import { useEffect, useState } from 'react'
import { isExpiringSoon, parseSignedMediaUrl } from '../../core/services/media-url-self-heal'
import { request as openApiRequest } from '../../../openapi/core/request'
import { useBrandKitStore } from '../store/brand-kit-store'

const cache = new Map<string, string>()
const inflight = new Map<string, Promise<string | undefined>>()

/** Cache a known display url so the canvas can show it without a round-trip. */
export function seedAttachmentUrl(attachmentId: string, url: string): void {
  if (attachmentId && url) cache.set(attachmentId, url)
}

// A cached signed url is only worth reusing while it has life left; a stale one
// would render a broken layer (the DOM self-heal would then fix it, but a fresh
// resolve here avoids the flash entirely).
function usableCached(attachmentId: string): string | undefined {
  const url = cache.get(attachmentId)
  if (!url) return undefined
  if (isExpiringSoon(parseSignedMediaUrl(url), Date.now())) {
    cache.delete(attachmentId)
    return undefined
  }
  return url
}

async function fetchAttachmentUrl(attachmentId: string): Promise<string | undefined> {
  const existing = inflight.get(attachmentId)
  if (existing) return existing
  // Inline signed url (not the download-url variant, which signs a
  // Content-Disposition: attachment response).
  const p = openApiRequest<{ url?: string }>(OpenAPI, {
    method: 'GET',
    url: '/v1/media/{id}/url',
    path: { id: attachmentId },
  })
    .then((res: { url?: string }) => {
      const url = res?.url
      if (url) cache.set(attachmentId, url)
      return url
    })
    .catch((err: unknown) => {
      console.error('useAttachmentUrl: failed to resolve attachment', attachmentId, err)
      return undefined
    })
    .finally(() => {
      inflight.delete(attachmentId)
    })
  inflight.set(attachmentId, p)
  return p
}

/** Returns a display url for the given attachment id, or undefined while it resolves. */
export function useAttachmentUrl(attachmentId: string | undefined): string | undefined {
  const fromStore = useBrandKitStore(s =>
    attachmentId
      ? s.brandKit.images.find(i => i.attachmentId === attachmentId)?.publicUrl
      : undefined
  )
  const [resolved, setResolved] = useState<string | undefined>(() =>
    attachmentId ? usableCached(attachmentId) : undefined
  )

  useEffect(() => {
    if (!attachmentId) return
    if (fromStore) {
      cache.set(attachmentId, fromStore)
      return
    }
    const cached = usableCached(attachmentId)
    if (cached) {
      setResolved(cached)
      return
    }
    let active = true
    fetchAttachmentUrl(attachmentId).then(url => {
      if (active && url) setResolved(url)
    })
    return () => {
      active = false
    }
  }, [attachmentId, fromStore])

  if (!attachmentId) return undefined
  return fromStore ?? cache.get(attachmentId) ?? resolved
}
